<template>
  <div v-if="showModal">
   <div class="fixed inset-0 z-50 bg-black/40" style="background-color: rgba(17,17,17,0.25)" @click="close"></div>
    <div role="dialog" aria-modal="true" aria-labelledby="added-to-cart-title"  tabindex="-1" class="cart-modal top-[10rem] fixed left-0 right-[2.07143rem] w-full  md:left-auto md:w-auto  bg-white text-gray-700  p-[2.07143rem] shadow-[0_0.428571rem_1.142857rem_rgba(0,0,0,0.16)] rounded-[0.428571rem] md:max-w-[75%] md:top-[10rem] z-[60]">
      <div 
        tabindex="0"
        @click="close"
        @keydown.esc.prevent="close"
        role="button"
        aria-label="Close added to cart dialog" 
        title="Close added to cart dialog" 
        id="cart-popup-close-button"
        class="absolute top-0 right-0 p-4 
              text-black font-bold 
              hover:text-gold-100 
              cursor-pointer">
        <Icon icon="close" class="mr-2" />
      </div>
      <div id="added-to-cart-title" class="flex items-center gap-4">
        <span class="inline-flex items-center justify-center w-5 h-5 mr-2 rounded-full bg-green-500">
          <Icon icon="check" class="text-white text-sm" />
        </span>
        <Heading :level="3">Added to Cart</Heading>
      </div>
      <div class="py-4 flex gap-4">
        <div class="p-3">
          <NuxtImg v-if="cartProduct.imageUrl" class="w-[9.285714rem] h-[9.285714rem] rounded bg-gray-100 p-4" 
            :src="cartProduct.imageUrl" 
            :alt="cartProduct.name ?? 'cart-image'" 
            :title="cartProduct.name ?? 'cart-image'" 
            id="cart-image"
          />
          <div v-else class="w-[9.285714rem] h-[9.285714rem] flex items-center justify-center rounded bg-gray-100 text-gray-400">No Image </div>
        </div>
        <div class="flex-1 p-3">
          <p class="font-semibold pb-2 mb-4 text-[1rem] border-b border-gray-400">
            {{ cartProduct.name ?? 'No product selected' }}
          </p>
          <div class="flex gap-4 text-sm">
            <span class="bg-gray-300 rounded px-3 py-1 text-[0.8571rem]">
              {{ cartProduct.subtotal ?? '--' }}
            </span>
            <p class="text-[0.8571rem]">
              Qty {{ cartProduct.quantity ?? '--' }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex gap-2 mt-4">
      <NuxtLink :to="URL_CONFIG.CART" :aria-label="`View cart ${cartQuantity}`" :title="`View cart ${cartQuantity}`" id="view-cart" class="cursor-pointer text-center py-[0.428571rem] px-[1rem] rounded-[0.42857rem] hover:text-gold-300 border" external>
        View Cart ({{ cartQuantity ?? 0 }})
      </NuxtLink>
      <NuxtLink @click.prevent="moveToCheckout" aria-label="Checkout" title="Checkout" id="checkout" class="cursor-pointer bg-black text-white text-center py-[0.428571rem] px-[1rem] rounded-[0.42857rem] hover:bg-gray-800" external>
        Checkout
      </NuxtLink>
      </div>
    </div>
    <div class="sr-only" aria-live="assertive">
      {{ showModal ? 'Cart popup opened' : '' }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import Heading from '../../atom/heading.vue'
import Icon from '../../atom/icon.vue'
import { type CartLine, useCartStore } from '~/stores/cartStore'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import { convertPriceStringToFloat } from '~~/shared/utils/price'
import type { CartProduct } from '~~/shared/types/common'

type CartClickstreamLine = CartLine & {
  unitPrice?: string | number
}

const showModal = ref(false)
const cartStore = useCartStore()
const cartProduct = ref<CartProduct>({
  name: null,
  subtotal: null,
  imageUrl: null,
  quantity: null,
  id: null
})
const cartQuantity = ref<number | null>(null)

const open = (product?: Partial<CartProduct>, quantity?: number) => {
  cartProduct.value = {
    name: product?.name ?? null,
    subtotal: product?.subtotal ?? null,
    imageUrl: product?.imageUrl ?? null,
    quantity: product?.quantity ?? null,
    id: product?.id ?? null
  }
  cartQuantity.value = quantity ?? null
  showModal.value = true
  nextTick(() => {
    const modal = document.querySelector('.cart-modal') as HTMLElement
    modal?.focus()
    document.addEventListener('keydown', trapFocus)
  })
}

const close = () => {
  showModal.value = false
  document.removeEventListener('keydown', trapFocus)
  nextTick(() => {
    const btn = document.getElementById(`${cartProduct.value?.id}`)
    btn?.focus()
  })
}

/**
 * Maps cart store lines into the item shape expected by the checkout_started clickstream event.
 */
const generateCartClickstreamLines = (cartLines: CartClickstreamLine[]) => {
  return cartLines.map((line) => {
    const parsedPrice = convertPriceStringToFloat(line.unitPrice ?? line.subtotal)

    return {
      product_id: String(line.productId),
      sku: line.sku,
      product_name: line.name,
      quantity: line.quantity,
      price: Number.isFinite(parsedPrice) ? parsedPrice : undefined
    }
  })
}

/**
 * Tracks checkout_started when cart lines are available, then immediately redirects to checkout.
 * Tracking is fire-and-forget so analytics failures do not block the checkout flow.
 */
const moveToCheckout = () => {
  const cartLines = cartStore.data?.lines ?? []

  if (cartLines.length > 0) {
    try {
      const result = useNuxtApp().$hightouchEvents?.track?.('checkout_started', {
        currency: 'USD',
        items: generateCartClickstreamLines(cartLines)
      })

      void Promise.resolve(result).catch((error) => {
        console.warn('Failed to track checkout_started clickstream event', error)
      })
    } catch (error) {
      console.warn('Failed to track checkout_started clickstream event', error)
    }
  }

  window.location.replace(URL_CONFIG.CHECKOUT)
}

const trapFocus = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    close()
    return
  }
  if (event.key !== 'Tab') return

  const modal = document.querySelector('.cart-modal') as HTMLElement
  if (!modal) return
  const focusableEls = modal.querySelectorAll<HTMLElement>(
    'button, [href], [tabindex]:not([tabindex="-1"])'
  )
  const firstEl = focusableEls[0]
  const lastEl = focusableEls[focusableEls.length - 1]

  if (event.shiftKey && document.activeElement === firstEl) {
    event.preventDefault()
    lastEl!.focus()
  } else if (!event.shiftKey && document.activeElement === lastEl) {
    event.preventDefault()
    firstEl!.focus()
  }
}

defineExpose({ open, close })

</script>
